"use client";

import React from "react";
import { ArrowUpRight, Clock, UserCheck } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
export default function Spotlight() {
  const shouldReduceMotion = useReducedMotion();
  const animationProps = shouldReduceMotion ? {
    initial: {
      opacity: 1
    },
    whileInView: {
      opacity: 1
    }
  } : {
    initial: {
      opacity: 0,
      y: 14
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      amount: 0.2
    },
    transition: {
      duration: 0.5,
      ease: [0.16, 1, 0.3, 1]
    }
  };
  return <section id="featured-article-spotlight" data-nav="light" className="border-b border-border bg-background py-16 sm:py-20 lg:py-24">
      <div className="mx-auto max-w-[1280px] px-5 sm:px-8 lg:px-12">
        {/* Masthead Rule */}
        <div className="mb-8 flex flex-col gap-1 border-b border-foreground/20 pb-4 sm:mb-12">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold tracking-wider text-muted-foreground uppercase">
              Editorial Spotlight
            </span>
            <span className="font-mono text-xs text-muted-foreground">
              Vol. 28 / Q3 Analysis
            </span>
          </div>
          <div className="h-[1px] w-full bg-border" />
        </div>

        {/* Spotlight Grid Container */}
        <motion.div {...animationProps} className="grid grid-cols-1 items-stretch gap-8 lg:grid-cols-12 lg:gap-12">
          {/* Article Image Column */}
          <div className="relative flex flex-col overflow-hidden rounded-md border border-border bg-card lg:col-span-6">
            <div className="relative aspect-[16/10] w-full overflow-hidden bg-muted lg:aspect-auto lg:h-full lg:min-h-[420px]">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/analysts_workspace_editorial.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/analysts_workspace_editorial.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/analysts_workspace_editorial.webp-wvc-sizes" alt="Technology analysts reviewing enterprise market models in a modern workspace" loading="lazy" decoding="async" className="h-full w-full object-cover" />
            </div>
          </div>

          {/* Article Content Column */}
          <div className="flex flex-col justify-between lg:col-span-6 lg:py-2">
            <div>
              {/* Category & Topic Badges */}
              <div className="mb-4 flex flex-wrap items-center gap-2.5">
                <Badge variant="secondary" className="rounded-full px-3 py-1 text-xs font-semibold tracking-wider uppercase text-secondary-foreground">
                  Enterprise Strategy
                </Badge>
                <span className="font-mono text-xs text-muted-foreground">
                  Flagship Brief
                </span>
              </div>

              {/* Headline */}
              <h2 className="font-serif text-2xl font-semibold tracking-tight text-foreground sm:text-3xl lg:text-4xl">
                The architectural shift in enterprise data governance
              </h2>

              {/* Lead Excerpt */}
              <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
                How technology leaders are restructuring centralized data lakes
                into domain-owned models to improve reporting accuracy and
                decision speed across corporate divisions.
              </p>

              {/* Key Highlights Bullet points */}
              <div className="mt-6 border-t border-border pt-5">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="flex items-start gap-2 text-sm text-foreground">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-foreground" />
                    <span>Peer-reviewed benchmark metrics</span>
                  </div>
                  <div className="flex items-start gap-2 text-sm text-foreground">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-foreground" />
                    <span>Executive implementation framework</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Bylines & Action Footer */}
            <div className="mt-8 border-t border-border pt-6">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-foreground">
                    <UserCheck className="size-5" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm font-semibold text-foreground">
                      Marcus Vance
                    </span>
                    <span className="font-mono text-xs text-muted-foreground">
                      Principal Systems Fellow
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Clock className="size-4" />
                    <span className="font-mono">8 min read</span>
                  </div>

                  <Button asChild size="default" className="rounded-sm font-medium">
                    <Link to="/IndustryInsightsPage" className="inline-flex items-center gap-1.5">
                      <span>Read article</span>
                      <ArrowUpRight className="size-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}